/*
 * /people/[personId] (page)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
	Badge,
	Button,
	ConfirmDialog,
	ConfirmDialogAction,
	ConfirmDialogCancel,
	ConfirmDialogContent,
	ConfirmDialogDescription,
	ConfirmDialogFooter,
	ConfirmDialogHeader,
	ConfirmDialogTitle,
	ConfirmDialogTrigger,
	Container,
	Divider,
	Page,
	PageHeader,
	Paper,
	Skeleton,
} from "@psychology/react-ui"
import { ArrowLeft, Pencil, Trash2, User } from "lucide-react"
import { PersonForm } from "../../features/people/components/person-form"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/people/$personId")({
	component: PersonDetailPage,
	loader: authPageLoader,
})

type DetailRowProps = {
	label: string
	value: string | number | boolean | null | undefined
}

function DetailRow({ label, value }: DetailRowProps) {
	if (value == null || value === "") {
		return null
	}
	const display =
		typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)
	return (
		<div className={"flex gap-2 text-sm"}>
			<span className={"text-muted-foreground w-40 shrink-0"}>{label}</span>
			<span className={"capitalize"}>{display}</span>
		</div>
	)
}

function PersonDetailPage() {
	const { personId } = Route.useParams()
	const { user } = Route.useLoaderData()
	const navigate = useNavigate()
	const utils = trpc.useUtils()

	const {
		data: person,
		isLoading,
		refetch,
	} = trpc.person.getByFilter.useQuery({
		where: { id: personId },
	})

	const deleteMutation = trpc.person.delete.useMutation({
		onSuccess: async () => {
			await utils.person.getAllByFilter.invalidate()
			navigate({ to: "/people" })
		},
	})

	if (isLoading) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<Skeleton className={"h-8 w-48"} />
					<Divider />
					<div className={"flex flex-col gap-4"}>
						<Skeleton className={"h-24 w-full"} />
						<Skeleton className={"h-20 w-full"} />
						<Skeleton className={"h-20 w-full"} />
					</div>
				</Container>
			</Page>
		)
	}

	if (!person) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<PageHeader
						title={"Person not found"}
						left={{
							href: "/people",
							label: "People",
							Icon: <ArrowLeft size={20} />,
						}}
					/>
					<Divider />
					<p className={"text-muted-foreground text-sm"}>
						{"This person could not be found."}
					</p>
				</Container>
			</Page>
		)
	}

	const displayLabel = person.displayName || person.name
	const nicknameList = (person.nicknames as string[] | null) ?? []

	const hasRelationshipContext =
		person.relationshipDetails || person.emotionalContext
	const hasAboutThem =
		person.roughAge != null ||
		person.knownSince ||
		person.occupation ||
		person.location ||
		person.theirRelationshipStatus != null ||
		person.isParent != null ||
		person.contactFrequency

	return (
		<Page centerVertical={false}>
			<Container width={"xl"} padding={"lg"} gap={"sm"}>
				<PageHeader
					title={displayLabel}
					left={{
						href: "/people",
						label: "People",
						Icon: <ArrowLeft size={20} />,
					}}
					right={{
						Component: (
							<div className={"flex items-center justify-end gap-2"}>
								<PersonForm
									mode={"edit"}
									userId={user.id}
									person={person}
									onSuccess={() => refetch()}
									trigger={
										<Button size={"sm"} variant={"outline"}>
											<Pencil size={14} className={"mr-1.5"} />
											{"Edit"}
										</Button>
									}
								/>
								<ConfirmDialog>
									<ConfirmDialogTrigger asChild>
										<Button
											size={"sm"}
											variant={"outline"}
											className={"text-destructive hover:text-destructive"}
										>
											<Trash2 size={14} className={"mr-1.5"} />
											{"Remove"}
										</Button>
									</ConfirmDialogTrigger>
									<ConfirmDialogContent>
										<ConfirmDialogHeader>
											<ConfirmDialogTitle>
												{"Remove person?"}
											</ConfirmDialogTitle>
											<ConfirmDialogDescription>
												{`This will permanently remove "${displayLabel}" and all their information. This cannot be undone.`}
											</ConfirmDialogDescription>
										</ConfirmDialogHeader>
										<ConfirmDialogFooter>
											<ConfirmDialogCancel>{"Cancel"}</ConfirmDialogCancel>
											<ConfirmDialogAction
												onClick={() => deleteMutation.mutate({ id: person.id })}
											>
												{"Remove"}
											</ConfirmDialogAction>
										</ConfirmDialogFooter>
									</ConfirmDialogContent>
								</ConfirmDialog>
							</div>
						),
					}}
				/>
				<Divider />

				<div className={"flex flex-col gap-4"}>
					{/* Identity */}
					<Paper className={"flex flex-col gap-4 p-5"}>
						<div className={"flex items-center gap-3"}>
							<div
								className={
									"bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
								}
							>
								<User size={22} className={"text-muted-foreground"} />
							</div>
							<div className={"flex flex-col gap-1"}>
								<div className={"flex flex-wrap items-center gap-2"}>
									<span className={"text-lg font-semibold"}>
										{displayLabel}
									</span>
									{person.relationship && (
										<Badge variant={"secondary"} className={"capitalize"}>
											{person.relationship}
										</Badge>
									)}
									{person.romanticContext && (
										<Badge variant={"outline"} className={"capitalize"}>
											{person.romanticContext}
										</Badge>
									)}
								</div>
								{nicknameList.length > 0 && (
									<p className={"text-muted-foreground text-sm"}>
										{`Also known as: ${nicknameList.join(", ")}`}
									</p>
								)}
							</div>
						</div>
					</Paper>

					{/* About Them */}
					{hasAboutThem && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"About Them"}
							</h3>
							<div className={"flex flex-col gap-2"}>
								<DetailRow
									label={"Rough Age"}
									value={person.roughAge != null ? `~${person.roughAge}` : null}
								/>
								<DetailRow label={"Known Since"} value={person.knownSince} />
								<DetailRow label={"Occupation"} value={person.occupation} />
								<DetailRow label={"Location"} value={person.location} />
								<DetailRow
									label={"Relationship Status"}
									value={person.theirRelationshipStatus}
								/>
								<DetailRow
									label={"Has Kids"}
									value={person.isParent != null ? person.isParent : null}
								/>
								<DetailRow
									label={"Contact Frequency"}
									value={person.contactFrequency}
								/>
							</div>
						</Paper>
					)}

					{/* Relationship Context */}
					{hasRelationshipContext && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"Relationship Context"}
							</h3>
							{person.relationshipDetails && (
								<div className={"flex flex-col gap-1"}>
									<span className={"text-muted-foreground text-xs font-medium"}>
										{"Details"}
									</span>
									<p className={"text-sm"}>{person.relationshipDetails}</p>
								</div>
							)}
							{person.emotionalContext && (
								<div className={"flex flex-col gap-1"}>
									<span className={"text-muted-foreground text-xs font-medium"}>
										{"Emotional Context"}
									</span>
									<p className={"text-sm"}>{person.emotionalContext}</p>
								</div>
							)}
						</Paper>
					)}

					{/* Notes */}
					{person.details && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"Notes"}
							</h3>
							<p className={"text-sm"}>{person.details}</p>
						</Paper>
					)}

					{/* Meta */}
					<Paper className={"flex flex-col gap-2 p-5"}>
						<h3
							className={
								"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
							}
						>
							{"Info"}
						</h3>
						<div className={"flex flex-col gap-1"}>
							<DetailRow
								label={"Added"}
								value={new Date(person.createdAt).toLocaleDateString(
									undefined,
									{
										year: "numeric",
										month: "long",
										day: "numeric",
									},
								)}
							/>
							<DetailRow
								label={"Last Updated"}
								value={new Date(person.updatedAt).toLocaleDateString(
									undefined,
									{
										year: "numeric",
										month: "long",
										day: "numeric",
									},
								)}
							/>
						</div>
					</Paper>
				</div>
			</Container>
		</Page>
	)
}
